/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, IconButton, SvgIcon, Typography } from '@mui/material';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { MutationDefinition } from '@reduxjs/toolkit/query';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Button,
  ConfirmationMessageModal,
  FullScreenLoader,
  Loader,
  TextFieldWrapperWithInputMask,
} from '@Components/index';
import MainSectionLayout from '@Components/MainSectionLayout';
import commonConstants from '@Constants/common.constants';
import Icons from '@Constants/Icons.constants';
import SharingNumberModal from '@Containers/UserCallerIds/NumberSharing/NumberSharingModal.container';
import SmsCodeVerificationModal from '@Containers/UserCallerIds/SmsCodeVerification';
import {
  StyledForm,
  StyledStack,
} from '@Containers/UserCallerIds/UserCallerIds.styles';
import { AddNewCallerIdSchema } from '@Containers/UserCallerIds/UserCallerIdsSchema';
import {
  AddNewNumberInputs,
  DeleteCallerIdRequest,
  ProviderCallerIdData,
  VerificationStatus,
  VerifyCallerRequest,
  VerifyCallerResponse,
  VoiceProvider,
} from '@Models/callerIds.models';
import { ScriptsQuery } from '@Models/scripts.models';
import {
  useDeleteCallerByIdMutation,
  useGetRotationCallerIdListQuery,
  useGetVerifiedCallersListQuery,
  useVerifyCallerStatusQuery,
  useVerifyCallerTelnyxMutation,
  useVerifyCallerTwilioMutation,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import {
  formatNumber,
  getDateMMDDYYYYHHMM,
  getRTKQuery,
  unformatUSNumber,
} from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

interface CallerIdsProps {
  provider: VoiceProvider;
}

const AbstractCallerIds: FunctionComponent<CallerIdsProps> = ({
  provider,
}): JSX.Element => {
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [showAddNewNumberForm, setShowAddNewNumberForm] = useState(false);
  const [disableVerifyButton, setDisableVerifyButton] = useState(false);
  const [callerIdToVerifyStatus, setCallerIdToVerifyStatus] =
    useState<string>('');
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [confirmationMessage, setConfirmationMessage] =
    useState<React.ReactNode | null>(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationModalData, setConfirmationModalData] =
    useState<DeleteCallerIdRequest>({
      id: '',
    });
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [isSmsCodeVerificationModalOpen, setIsSmsCodeVerificationModalOpen] =
    useState(false);
  const [isCallerIdInvalidated, setIsCallerIdInvalidated] = useState(false);
  const [numberToShare, setNumberToShare] = useState<string>('');
  const [verificationStatus, setVerificationStatus] = useState<{
    status: VerificationStatus | null;
  }>({ status: null });
  const [pinCode, setPinCode] = useState('');

  const location = useLocation();
  const rotationQuery: ScriptsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );
  /** RTK QUERY HOOK */
  // load a list of verified callers
  const {
    data: dataOfVerifiedCallerList = [],
    isLoading: loadingVerifyCallerList,
    isFetching: fetchingVerifyCallerList,
    isError: isErrorForGetVerifiedCallerList,
    error: errorForGetVerifiedCallerList,
    refetch: refetchVerifiedCallersList,
  } = useGetVerifiedCallersListQuery({});

  const resolveVerifyCallerMutation = (): UseMutation<
    MutationDefinition<
      VerifyCallerRequest,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
      | 'CommonCallerIds'
      | 'RotationCallerId'
      | 'ForwardingNumber'
      | 'TprSettings',
      VerifyCallerResponse,
      'baseApi'
    >
  > => {
    // @ts-ignore
    const map: Record<VoiceProvider, any> = {
      [VoiceProvider.TWILIO]: useVerifyCallerTwilioMutation,
      [VoiceProvider.TELNYX]: useVerifyCallerTelnyxMutation,
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const key = map[provider];
    if (!key) {
      throw new Error(`Unsupportable voice provider: ${provider}`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return key;
  };

  const [verifyCaller, verifyCallerMethods] = resolveVerifyCallerMutation()();
  const [deleteCallerById, deleteCallerByIdMethods] =
    useDeleteCallerByIdMutation();
  const {
    data: dataForVerifyCallerStatus,
    isLoading: loadingForVerifyCallerStatus,
    isSuccess: successVerifyCallerStatus,
    isFetching: fetchingVerifyCallerStatus,
    error: errorOnVerifyCallerStatus,
  } = useVerifyCallerStatusQuery(callerIdToVerifyStatus, {
    skip: !callerIdToVerifyStatus,
    pollingInterval: commonConstants.POLLING_INTERVAL,
  });
  const { refetch: refetchRotationCallerIdList } =
    useGetRotationCallerIdListQuery({
      userId: rotationQuery.userId?.toString() || '',
      provider: provider.toString(),
    });

  const resetFromCallback = useCallback(() => {
    setPinCode('');
    setCallerIdToVerifyStatus('');
    setVerificationStatus({
      status: null,
    });
    setShowAddNewNumberForm(false);
  }, []);

  useEffect(() => {
    if (
      verificationStatus.status === VerificationStatus.INITIATED &&
      provider === VoiceProvider.TELNYX
    ) {
      setIsSmsCodeVerificationModalOpen(true);
    }
  }, [verificationStatus]);

  // refetch callerIds list on successfully added a sharing number
  useEffect(() => {
    if (isCallerIdInvalidated) {
      resetFromCallback();
      setNumberToShare('');
      setIsCallerIdInvalidated(false);

      refetchVerifiedCallersList().catch(console.error);
      refetchRotationCallerIdList().catch(console.error);
    }
  }, [
    isCallerIdInvalidated,
    refetchRotationCallerIdList,
    refetchVerifiedCallersList,
    resetFromCallback,
  ]);

  /** Table Loading State */
  const loading = loadingVerifyCallerList || fetchingVerifyCallerList;

  /** Page Heading */
  const headingText = t('userCallerIds.User Caller IDs');

  /**
   * Deletes the caller id
   * @param id Id of the caller
   * @param phoneNumber phone number of the caller id
   */
  const toggleDeleteConfirmation = (id: string, phoneNumber: string) => {
    setConfirmationModalData({ id });
    // Formatting phone to US Format
    const message: React.ReactNode = (
      <Typography
        variant="main"
        sx={{
          '& .phone': {
            color: 'secondary.main',
          },
        }}
      >
        <Trans
          i18nKey="common.confirmationMessages.confirmDelete"
          values={formatNumber(phoneNumber)}
          components={{ strong: <strong />, span: <span className="phone" /> }}
        />
      </Typography>
    );
    setConfirmationMessage(message);
    setIsConfirmationModalOpen(true);
  };

  /**
   * Hides the confirmation modal and deletes the caller id
   * @param data DeleteCallerIdRequest object
   * @returns delete mutation
   */
  const deleteActionHandler = (data: DeleteCallerIdRequest) => {
    setConfirmationModalData({ id: '' });
    setIsConfirmationModalOpen(false);
    if (data.id) {
      return deleteCallerById(data);
    }
  };

  /**
   * Add New Number Form Methods: Handles form validation using useForm hook
   */
  const methods = useForm<AddNewNumberInputs>({
    resolver: yupResolver(AddNewCallerIdSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /**
   * Toggles between hiding and showing the add new number form
   */
  const addNewNumber = () => {
    methods.reset({ phone: '' });
    setShowAddNewNumberForm(true);
  };

  /**
   * Error Alerts for the Get Caller Ids Query
   */
  useEffect(() => {
    if (isErrorForGetVerifiedCallerList && errorForGetVerifiedCallerList) {
      setDisableVerifyButton(false);
      triggerAlert(errorForGetVerifiedCallerList, setAlertState);
    }
  }, [
    triggerAlert,
    errorForGetVerifiedCallerList,
    isErrorForGetVerifiedCallerList,
  ]);

  /**
   * Add New Number Form Methods: Updates the form state on submit
   */
  const onSubmitHandler: SubmitHandler<AddNewNumberInputs> = (formData) => {
    if (formData) {
      setPinCode('');
      setVerificationStatus({
        status: null,
      });

      const formattedPhone = formData.phone;
      let planePhone: string;

      // Extract digits from entered phone number
      const filteredDigits = formattedPhone.replace(/\D/g, '');

      if (filteredDigits.startsWith('0')) {
        planePhone = `+38${unformatUSNumber(formattedPhone)}`;
      } else {
        planePhone = `+1${unformatUSNumber(formattedPhone)}`;
      }

      // Call API only if number is valid {10 digits}
      if (filteredDigits.length === commonConstants.PHONE_NUMBER_DIGITS_COUNT) {
        setDisableVerifyButton(true);
        setNumberToShare(planePhone);
        return verifyCaller({ phone: planePhone });
      }

      // Show form error for Invalid Number
      methods.setError('phone', {
        type: 'manual',
        message: t('userCallerIds.Invalid input'),
      });
    }
  };

  /**
   * Error and Success alerts for Verify Number
   */
  useEffect(() => {
    if (verifyCallerMethods.error) {
      setDisableVerifyButton(false);
      if ('data' in verifyCallerMethods.error) {
        if (
          // @ts-ignore
          verifyCallerMethods.error.data.message ===
          'CALLER_ID_ALREADY_VERIFIED'
        ) {
          setIsSharingModalOpen(true);
        } else {
          triggerAlert(verifyCallerMethods.error, setAlertState);
        }
      }
    }
    if (verifyCallerMethods.isSuccess) {
      setPinCode(verifyCallerMethods.data.pin);
      setVerificationStatus({ status: VerificationStatus.INITIATED });
      // poll
      setCallerIdToVerifyStatus(verifyCallerMethods.data.identifier);
    }
  }, [
    verifyCallerMethods,
    verifyCallerMethods.isSuccess,
    verifyCallerMethods.error,
    verifyCallerMethods.data,
    triggerAlert,
    methods,
    t,
  ]);

  /**
   * Error and Success alerts for Delete Caller
   */
  useEffect(() => {
    if (deleteCallerByIdMethods.isError && deleteCallerByIdMethods.error) {
      triggerAlert(deleteCallerByIdMethods.error, setAlertState);
    }
    if (deleteCallerByIdMethods.isSuccess) {
      triggerAlert(
        commonConstants.GLOBAL_MESSAGES.COMMON_CALLER_ID_DELETE,
        setAlertState
      );
    }
  }, [
    deleteCallerByIdMethods.isError,
    deleteCallerByIdMethods.isSuccess,
    deleteCallerByIdMethods.error,
    deleteCallerByIdMethods.data,
    triggerAlert,
  ]);

  /**
   * Verifying Caller Status
   * This uses a polling API endpoint
   */
  useEffect(() => {
    if (errorOnVerifyCallerStatus) {
      setDisableVerifyButton(false);
      setVerificationStatus({
        status: VerificationStatus.FAILED,
      });
      setCallerIdToVerifyStatus('');
    }
    if (successVerifyCallerStatus) {
      if (
        dataForVerifyCallerStatus?.status === VerificationStatus.VERIFIED &&
        !(loadingForVerifyCallerStatus || fetchingVerifyCallerStatus)
      ) {
        setDisableVerifyButton(false);
        setVerificationStatus({
          status: VerificationStatus.VERIFIED,
        });
        resetFromCallback();
        triggerAlert(
          commonConstants.GLOBAL_MESSAGES.COMMON_CALLER_ID_VERIFY,
          setAlertState
        );
        refetchVerifiedCallersList().catch(console.error);
        refetchRotationCallerIdList().catch(console.error);
      } else if (
        dataForVerifyCallerStatus?.status === VerificationStatus.FAILED &&
        !(loadingForVerifyCallerStatus || fetchingVerifyCallerStatus)
      ) {
        setDisableVerifyButton(false);
        setVerificationStatus({
          status: VerificationStatus.FAILED,
        });
        setCallerIdToVerifyStatus('');
      }
    }
  }, [
    resetFromCallback,
    triggerAlert,
    refetchVerifiedCallersList,
    dataForVerifyCallerStatus,
    loadingForVerifyCallerStatus,
    successVerifyCallerStatus,
    fetchingVerifyCallerStatus,
    errorOnVerifyCallerStatus,
    refetchRotationCallerIdList,
  ]);

  const cancelAddNewNumber = () => {
    resetFromCallback();
  };

  /**
   * Constructs the UI (row) of the caller table
   * @param id unique id of the caller
   * @param phoneStr
   * @param date createdAt date
   * @param isRevereficationRequired
   * @param deleteCallerId callback method to delete the caller by unique id
   * @returns a single row, populating the data
   */
  const CallerTableRow = (
    id: string,
    phoneStr: string,
    date: string,
    isRevereficationRequired: boolean,
    deleteCallerId: (id: string, phone: string) => void
  ): JSX.Element => (
    <StyledStack key={id}>
      <Box className="phone-and-warning">
        <Typography className="phone-number" variant="main">
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {formatNumber(phoneStr)}
        </Typography>
        {isRevereficationRequired && (
          <Typography className="warning-text" variant="main">
            This Caller Id should be reverified
          </Typography>
        )}
      </Box>
      <Typography className="date" variant="main">
        {t('userCallerIds.Created Date', {
          interpolation: {
            escapeValue: false,
          },
          date: getDateMMDDYYYYHHMM(new Date(date), true),
        })}
      </Typography>
      <IconButton
        onClick={() => deleteCallerId(id, phoneStr)}
        className="delete-icon"
        aria-label="delete"
      >
        <SvgIcon>{Icons.delete}</SvgIcon>
      </IconButton>
    </StyledStack>
  );

  const resolveVerifyMessageKey = (): string => {
    // @ts-ignore
    const map: Record<VoiceProvider, string> = {
      [VoiceProvider.TWILIO]: 'userCallerIds.Twilio Verify Message',
      [VoiceProvider.TELNYX]: 'userCallerIds.Telnyx Verify Message',
    };

    const key = map[provider];
    if (!key) {
      throw new Error(`Unsupportable voice provider: ${provider}`);
    }

    return key;
  };

  const resolveVerifyInProgressMessageKey = (): string => {
    // @ts-ignore
    const map: Record<VoiceProvider, string> = {
      [VoiceProvider.TWILIO]: 'userCallerIds.Twilio Verify In Progress',
      [VoiceProvider.TELNYX]: 'userCallerIds.Telnyx Verify In Progress',
    };

    const key = map[provider];
    if (!key) {
      throw new Error(`Unsupportable voice provider: ${provider}`);
    }

    return key;
  };

  /**
   * Conditionally shows/hides the Add New Number form
   */
  const AddNewNumberForm = (
    <>
      {/* Initially, the button will be displayed */}
      {!showAddNewNumberForm && (
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Button onClick={() => addNewNumber()} icon="add">
            {t('common.messages.Add Number')}
          </Button>
        </Box>
      )}

      {/* Clicking on the "Add Number" button will hide the button and show the form. */}
      {showAddNewNumberForm && (
        <>
          <Typography className="phone-number" mb={1.6} variant="main">
            {t(resolveVerifyMessageKey())}
          </Typography>
          <FormProvider {...methods}>
            <StyledForm
              component="form"
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <Box display="flex">
                <TextFieldWrapperWithInputMask
                  name="phone"
                  placeholder={`${t('userCallerIds.Enter your number')} *`}
                  className="add-number-input"
                  mask={commonConstants.PHONE_NUMBER_INPUT_MASK}
                />

                {/* Verification in progress */}
                {VerificationStatus.INITIATED === verificationStatus.status &&
                  provider === VoiceProvider.TWILIO && (
                    <Box display="flex" alignItems="center" ml={1.6}>
                      <Loader size="2.4rem" sx={{ mr: 1.6 }} />
                      <Typography
                        className="verification-initiated"
                        variant="main"
                      >
                        <Trans
                          i18nKey={resolveVerifyInProgressMessageKey()}
                          values={{ pinCode }}
                          components={{ span: <span className="pincode" /> }}
                        />
                      </Typography>
                    </Box>
                  )}

                {/* Verification failed */}
                {VerificationStatus.FAILED === verificationStatus.status && (
                  <Box display="flex" alignItems="center" ml={1.6}>
                    <Typography
                      className="verification-initiated-failed"
                      variant="main"
                    >
                      {t('userCallerIds.Verification Failed')}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box>
                <Button
                  variant="text"
                  type="button"
                  color="secondary"
                  sx={{ mr: 2.4 }}
                  disabled={loading || disableVerifyButton} // too many edge cases to handle for Cancel
                  onClick={() => cancelAddNewNumber()}
                >
                  {t('common.confirmationModal.CANCEL')}
                </Button>
                <Button type="submit" disabled={loading || disableVerifyButton}>
                  {VerificationStatus.FAILED === verificationStatus.status
                    ? t('userCallerIds.REVERIFY')
                    : t('userCallerIds.VERIFY')}
                </Button>
              </Box>
            </StyledForm>
          </FormProvider>
        </>
      )}
    </>
  );

  /**
   * Table component to render Phone, Created At date and Delete
   */
  const TableComponent = (
    <>
      {dataOfVerifiedCallerList.length > 0 &&
        dataOfVerifiedCallerList.map(
          (item: ProviderCallerIdData): JSX.Element =>
            CallerTableRow(
              item.id,
              item.callerId,
              item.createdAt,
              item.isRevereficationRequired,
              toggleDeleteConfirmation
            )
        )}
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={confirmationMessage}
        onClick={() => deleteActionHandler(confirmationModalData)}
      />
      <SharingNumberModal
        isOpen={isSharingModalOpen}
        setIsOpen={setIsSharingModalOpen}
        numberToShare={numberToShare}
        setIsCallerIdInvalidated={setIsCallerIdInvalidated}
        voiceProvider={provider}
      />
      <SmsCodeVerificationModal
        phone={methods.watch('phone') || ''}
        isOpen={isSmsCodeVerificationModalOpen}
        setIsOpen={setIsSmsCodeVerificationModalOpen}
        setDisableVerifyButton={setDisableVerifyButton}
        setIsCallerIdInvalidated={setIsCallerIdInvalidated}
      />
      {dataOfVerifiedCallerList.length === 0 && (
        <Typography
          variant="main"
          fontWeight="bold"
          textAlign="center"
          sx={{ my: 3.2, color: 'action.disabled' }}
        >
          {t('common.table.No data to show')}
        </Typography>
      )}
    </>
  );

  return (
    <>
      {loading ? (
        <FullScreenLoader data-testid="loader" />
      ) : (
        <>
          <MainSectionLayout
            alertState={alertState}
            heading={headingText}
            filters={AddNewNumberForm}
            table={TableComponent}
          />
        </>
      )}
    </>
  );
};

export default AbstractCallerIds;
